import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import i18next from "i18next";
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import 'moment/locale/fr';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Avatar from '@material-ui/core/Avatar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import Chip from '@material-ui/core/Chip';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Autocomplete from '@material-ui/lab/Autocomplete';
import EditIcon from '@material-ui/icons/Edit';
import DoneIcon from '@material-ui/icons/Done';
import BackIcon from '@material-ui/icons/KeyboardArrowLeft';

import httpClient from '../_shared/HttpClient';
import ScrollBody from '../_shared/ScrollBody';
import Loader from '../_shared/Loader';
import { convertMillisToTime } from '../_shared/StringFormat';

import { appStore } from '../../appStore.js';

const useStyles = makeStyles(theme => ({
    tabs: {
        marginBottom: theme.spacing(1),
    },
    avatarCaption: {
        fontSize: '60%',
        fontStyle: 'italic',
    },
    title: {
        fontSize: '110%',
    },
    subtitle: {
        paddingTop: -3,
        marginTop: -3,
        fontSize: '85%',
        fontStyle: 'italic',
    },
    smallAvatar: {
        width: props => theme.spacing(4),
        height: props => theme.spacing(4),
    },
    addButtonWrap: {
        padding: theme.spacing(1, 0),
        display: 'flex',
        justifyContent: 'center',
    },
    dialogTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    helperText: {
        marginTop: theme.spacing(1),
    },
    lineActions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: theme.spacing(0.5),
    },
    lineUpSection: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    lineUpHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        padding: theme.spacing(1, 2),
    },
    lineUpHeaderMain: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    lineUpEmpty: {
        padding: theme.spacing(2),
        fontStyle: 'italic',
    },
}));

const Summary = (props) => {
    const { state, dispatch } = useContext(appStore);
    const { leagueID, gameID } = props.match.params;
    const { t } = useTranslation();
    const classes = useStyles(state);
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState(null);

    const [goalDialogOpen, setGoalDialogOpen] = useState(false);
    const [editingGoalEventID, setEditingGoalEventID] = useState(null);
    const [goalEventTypeID, setGoalEventTypeID] = useState(0);
    const [teamPlayers, setTeamPlayers] = useState([]);
    const [selectedTeamID, setSelectedTeamID] = useState('');
    const [selectedPlayerID, setSelectedPlayerID] = useState('');
    const [selectedAssistPlayerID1, setSelectedAssistPlayerID1] = useState('');
    const [selectedAssistPlayerID2, setSelectedAssistPlayerID2] = useState('');
    const [period, setPeriod] = useState(1);
    const [timeText, setTimeText] = useState('00:01:00');
    const [manualTimeEdit, setManualTimeEdit] = useState(false);

    const [presenceDialogOpen, setPresenceDialogOpen] = useState(false);
    const [presenceDialogData, setPresenceDialogData] = useState({ teams: [], positions: [] });
    const [presencePlayerOptions, setPresencePlayerOptions] = useState([]);
    const [presencePlayerSearch, setPresencePlayerSearch] = useState('');
    const [selectedPresencePlayer, setSelectedPresencePlayer] = useState(null);
    const [selectedPresenceDomVis, setSelectedPresenceDomVis] = useState('');
    const [selectedPresencePositionID, setSelectedPresencePositionID] = useState('');
    const [presenceStatus, setPresenceStatus] = useState(100);
    const [isSearchingPresencePlayers, setIsSearchingPresencePlayers] = useState(false);

    useEffect(() => {
        dispatch({ type: 'updateNavigation', payload: { navTitle: 'common:summary' } });
        return () => {
        }
    }, []);

    useEffect(() => {
        props.setLeagueID(leagueID);
        if (!gameID) return;
        loadData();
    }, [leagueID, gameID]);

    useEffect(() => {
        if (!goalDialogOpen || manualTimeEdit || editingGoalEventID) return;
        setTimeText(formatMillisToHhMmSs(getSuggestedTimeMs(period)));
    }, [period, goalDialogOpen, manualTimeEdit, editingGoalEventID, data]);

    useEffect(() => {
        if (!presenceDialogOpen) return;

        const timeoutId = setTimeout(() => {
            searchPresencePlayers(presencePlayerSearch);
        }, 250);

        return () => clearTimeout(timeoutId);
    }, [presenceDialogOpen, presencePlayerSearch]);

    const localizeText = (value) => {
        if (!value) return '';

        try {
            const parsed = JSON.parse(value);
            const language = i18next.languages && i18next.languages.length ? i18next.languages[0] : i18next.language;
            return parsed[language] || parsed.fr || parsed.en || value;
        } catch {
            return value;
        }
    };

    const formatMillisToHhMmSs = (ms) => {
        const totalSeconds = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
        const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
        const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
        const seconds = String(totalSeconds % 60).padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
    };

    const parseHhMmSsToMillis = (value) => {
        if (!value) return null;

        const cleaned = String(value).trim();
        const parts = cleaned.split(':').map(x => x.trim());
        if (parts.length !== 3) return null;

        const [h, m, s] = parts.map(x => parseInt(x, 10));
        if ([h, m, s].some(x => Number.isNaN(x) || x < 0) || m > 59 || s > 59) return null;

        return ((h * 3600) + (m * 60) + s) * 1000;
    };

    const formatPresencePlayerLabel = (player) => {
        if (!player) return '';
        const localizedPosition = localizeText(player.positionName);
        return `${player.name}${player.defaultNumber ? ' #' + player.defaultNumber : ''}${localizedPosition ? ' - ' + localizedPosition : ''}`;
    };

    const allEvents = data && data.eventTypes
        ? data.eventTypes.reduce((acc, et) => acc.concat(et.events || []), [])
        : [];
    const eventTabs = data && data.eventTypes ? data.eventTypes : [];
    const lineUpTabIndex = eventTabs.length;
    const lineUps = data && data.lineUps ? data.lineUps : [];

    const getDefaultPeriod = () => {
        if (!allEvents.length) return 1;
        const maxPeriod = Math.max(...allEvents.map(e => Number(e.period) || 1));
        return maxPeriod > 0 ? maxPeriod : 1;
    };

    const getSuggestedTimeMs = (periodValue) => {
        const targetPeriod = Number(periodValue) || 1;
        const eventsInPeriod = allEvents.filter(e => (Number(e.period) || 1) === targetPeriod);
        const maxMs = eventsInPeriod.length ? Math.max(...eventsInPeriod.map(e => Number(e.time) || 0)) : 0;
        return maxMs + 60000;
    };

    const loadData = async () => {
        try {
            setIsLoading(true);
            const result = await httpClient('get', 'Calendar/GetSummaryAndEvents', { Id: gameID });
            setData(result);
        }
        catch (err) {
            console.log(err);
        }
        setIsLoading(false);
    };

    const openGoalDialog = async (eventTypeID, eventToEdit = null) => {
        try {
            const result = await httpClient('get', 'Calendar/GetGoalPlayers', { gameID: gameID });
            const teams = result || [];

            setTeamPlayers(teams);
            setGoalEventTypeID(eventTypeID);
            setManualTimeEdit(false);

            if (eventToEdit) {
                setEditingGoalEventID(eventToEdit.eventID);
                setSelectedTeamID(eventToEdit.team && eventToEdit.team.teamID ? eventToEdit.team.teamID : '');
                setSelectedPlayerID(eventToEdit.playerID || '');
                setSelectedAssistPlayerID1(eventToEdit.assistPlayerIDs && eventToEdit.assistPlayerIDs[0] ? eventToEdit.assistPlayerIDs[0] : '');
                setSelectedAssistPlayerID2(eventToEdit.assistPlayerIDs && eventToEdit.assistPlayerIDs[1] ? eventToEdit.assistPlayerIDs[1] : '');
                setPeriod(eventToEdit.period || 1);
                setTimeText(formatMillisToHhMmSs(eventToEdit.time || 0));
            } else {
                const defaultPeriod = getDefaultPeriod();
                setEditingGoalEventID(null);
                setSelectedTeamID(teams.length > 0 && teams[0].team ? teams[0].team.teamID : '');
                setSelectedPlayerID('');
                setSelectedAssistPlayerID1('');
                setSelectedAssistPlayerID2('');
                setPeriod(defaultPeriod);
                setTimeText(formatMillisToHhMmSs(getSuggestedTimeMs(defaultPeriod)));
            }

            setGoalDialogOpen(true);
        } catch (err) {
            console.log(err);
        }
    };

    const openPresenceDialog = async (defaultDomVis = '') => {
        try {
            const result = await httpClient('get', 'Calendar/GetPresenceDialogData', { gameID });
            const dialogData = result || { teams: [], positions: [] };
            const initialDomVis = defaultDomVis || (dialogData.teams && dialogData.teams.length > 0 ? dialogData.teams[0].domVis : '');

            setPresenceDialogData(dialogData);
            setSelectedPresenceDomVis(initialDomVis);
            setSelectedPresencePlayer(null);
            setPresencePlayerOptions([]);
            setPresencePlayerSearch('');
            setSelectedPresencePositionID('');
            setPresenceStatus(100);
            setPresenceDialogOpen(true);
        } catch (err) {
            console.log(err);
        }
    };

    const closePresenceDialog = () => {
        setPresenceDialogOpen(false);
        setSelectedPresencePlayer(null);
        setPresencePlayerOptions([]);
        setPresencePlayerSearch('');
        setSelectedPresencePositionID('');
        setPresenceStatus(100);
    };

    const searchPresencePlayers = async (searchString) => {
        try {
            setIsSearchingPresencePlayers(true);
            const result = await httpClient('get', 'Calendar/SearchLeaguePlayersForPresence', {
                gameID,
                searchString: searchString || '',
                take: 20,
            });
            setPresencePlayerOptions(result || []);
        } catch (err) {
            console.log(err);
        }
        setIsSearchingPresencePlayers(false);
    };

    const submitPresence = async () => {
        if (!selectedPresenceDomVis || !selectedPresencePlayer) return;

        try {
            await httpClient('post', `Calendar/AddPresence?gameID=${gameID}`, {
                domVis: Number(selectedPresenceDomVis),
                playerID: selectedPresencePlayer.playerID,
                positionID: selectedPresencePositionID ? Number(selectedPresencePositionID) : null,
                status: Number(presenceStatus) || 100,
            });

            closePresenceDialog();
            await loadData();
        } catch (err) {
            console.log(err);
            window.alert('Impossible d\'ajouter cette présence. Vérifiez si elle existe déjà ou si le joueur appartient bien à la ligue.');
        }
    };

    const selectedTeam = teamPlayers.find(x => x.team && x.team.teamID === selectedTeamID);
    const playersForTeam = selectedTeam && selectedTeam.players ? selectedTeam.players : [];
    const assistPlayersForTeam = playersForTeam.filter(p => p.playerID !== selectedPlayerID);

    const submitGoal = async () => {
        if (!selectedTeamID || !selectedPlayerID || !goalEventTypeID) return;

        const parsedTime = parseHhMmSsToMillis(timeText);
        const finalTime = parsedTime == null ? getSuggestedTimeMs(period) : parsedTime;

        const assistPlayerIDs = [selectedAssistPlayerID1, selectedAssistPlayerID2]
            .filter(x => !!x && x !== selectedPlayerID)
            .filter((x, index, arr) => arr.indexOf(x) === index);

        try {
            if (editingGoalEventID) {
                await httpClient('put', `Calendar/UpdateGoal?gameID=${gameID}&eventID=${editingGoalEventID}`, {
                    teamID: selectedTeamID,
                    playerID: selectedPlayerID,
                    eventTypeID: goalEventTypeID,
                    period: period,
                    time: finalTime,
                    assistPlayerIDs: assistPlayerIDs,
                });
            } else {
                await httpClient('post', `Calendar/AddGoal?gameID=${gameID}`, {
                    teamID: selectedTeamID,
                    playerID: selectedPlayerID,
                    eventTypeID: goalEventTypeID,
                    period: period,
                    time: finalTime,
                    assistPlayerIDs: assistPlayerIDs,
                });
            }

            setGoalDialogOpen(false);
            setEditingGoalEventID(null);
            await loadData();
        } catch (err) {
            console.log(err);
        }
    };

    if (isLoading) {
        return <Loader isLoading={isLoading}><div></div></Loader>
    }

    return (
        <>
            <ListItem button dense disableGutters divider component={Link} to={"/home/calendar/" + leagueID} onClick={() => dispatch({ type: 'updateSummaryIndex', payload: { index: 0 } })}>
                <ListItemAvatar>
                    <Avatar className={classes.smallAvatar}>
                        <BackIcon />
                    </Avatar>
                </ListItemAvatar>
                <ListItemText
                    primary={data ? data.title : ''}
                    secondary={
                        <Moment locale={i18next.language} format="ddd Do MMMM YYYY - HH:mm" date={data ? data.gameDateTime : null} />
                    } />
            </ListItem>

            <Tabs
                value={state.summaryIndex}
                onChange={(e, v) => dispatch({ type: 'updateSummaryIndex', payload: { index: v } })}
                variant={"scrollable"}
                className={classes.tabs}
            >
                {
                    eventTabs.map((x, i) =>
                        <Tab
                            key={i}
                            label={localizeText(x.name)}
                        />
                    )
                }
                <Tab label={t('common:lineUp')} />
            </Tabs>

            <ScrollBody
                hasMorePrevious={isLoading}
                hasMoreNext={false}
            >
                {
                    eventTabs.map((x, i) =>
                        <div key={'events_' + i}
                            hidden={i !== state.summaryIndex}
                        >
                            {
                                (x.events ? x.events : []).map((e) =>

                                    <ListItem
                                        key={e.eventID}
                                        component={Link}
                                        to={e.hasVideos ? `/home/calendar/${leagueID}/summary/${gameID}/event/${e.eventID}` : `/home/calendar/${leagueID}/summary/${gameID}`}
                                        button={e.hasVideos}
                                        divider>

                                        <Grid
                                            container
                                            alignItems="center"
                                            spacing={1}
                                        >
                                            <Grid item xs={3} sm={2} align={"center"}>

                                                <Avatar src={e.team ? e.team.logoUrl : ''} />

                                                <Typography noWrap variant={'caption'} className={classes.avatarCaption}>
                                                    {e.team ? e.team.name : ''}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs sm={6}>
                                                <Typography variant={'caption'}>
                                                    <div className={classes.title}>
                                                        {e.title}
                                                    </div>
                                                    {
                                                        x.showOnlyPlayer ? '' :
                                                            <>
                                                                <div className={classes.subtitle}>
                                                                    {localizeText(e.subtitle)}
                                                                </div>

                                                                <div className={classes.subtitle}>
                                                                    {e.details ? t('actions:assist') + e.details : ''}
                                                                </div>
                                                            </>
                                                    }
                                                </Typography>
                                            </Grid>

                                            {
                                                x.showOnlyPlayer ? '' :
                                                    <Grid item xs={3} sm={3}>
                                                        <div className={classes.lineActions}>
                                                            <Chip
                                                                size={"small"}
                                                                avatar={<Avatar>{e.period}</Avatar>}
                                                                label={convertMillisToTime(e.time)}
                                                            />

                                                            {
                                                                data && data.canAddGoal && x.gameValue > 0 ?
                                                                    <Tooltip title="Modifier le but">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={(evt) => {
                                                                                evt.preventDefault();
                                                                                evt.stopPropagation();
                                                                                openGoalDialog(x.eventTypeID, e);
                                                                            }}
                                                                        >
                                                                            <EditIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    : null
                                                            }
                                                        </div>
                                                    </Grid>
                                            }

                                        </Grid>
                                    </ListItem>
                                )
                            }

                            {
                                data && data.canAddGoal && x.gameValue > 0 && x.eventTypeID > 0 ?
                                    <div className={classes.addButtonWrap}>
                                        <Button variant="outlined" color="primary" onClick={() => openGoalDialog(x.eventTypeID)}>+</Button>
                                    </div>
                                    : null
                            }
                        </div>
                    )
                }

                <div hidden={state.summaryIndex !== lineUpTabIndex}>
                    {
                        lineUps.map((lineUp, index) =>
                            <div key={'lineup_' + index} className={classes.lineUpSection}>
                                <div className={classes.lineUpHeader}>
                                    <div className={classes.lineUpHeaderMain}>
                                        <Avatar src={lineUp.team ? lineUp.team.logoUrl : ''} className={classes.smallAvatar} />
                                        <Typography variant={'h6'}>
                                            {lineUp.team ? lineUp.team.name : ''}
                                        </Typography>
                                    </div>

                                    {
                                        data && data.canAddGoal ?
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                onClick={() => openPresenceDialog(lineUp.domVis)}
                                            >
                                                +
                                            </Button>
                                            : null
                                    }
                                </div>

                                {
                                    lineUp.players && lineUp.players.length > 0 ?
                                        lineUp.players.map((player) =>
                                            <ListItem key={`lineup_player_${lineUp.domVis}_${player.playerID}_${player.positionCode || 'nopos'}`} divider>
                                                <Grid container alignItems="center" spacing={1}>
                                                    <Grid item xs={12}>
                                                        <Typography variant={'body2'}>
                                                            {`${player.name}${player.defaultNumber ? ' #' + player.defaultNumber : ''}`}
                                                        </Typography>
                                                        {(localizeText(player.positionName) || player.positionCode) ? (
                                                            <Typography variant={'caption'} className={classes.subtitle}>
                                                                {localizeText(player.positionName) || player.positionCode}
                                                            </Typography>
                                                        ) : null}
                                                    </Grid>
                                                </Grid>
                                            </ListItem>
                                        )
                                        :
                                        <Typography variant={'body2'} color={'textSecondary'} className={classes.lineUpEmpty}>
                                            Aucun joueur
                                        </Typography>
                                }
                            </div>
                        )
                    }
                </div>

            </ScrollBody>

            <Dialog open={goalDialogOpen} onClose={() => { setGoalDialogOpen(false); setEditingGoalEventID(null); }} fullWidth maxWidth="sm">
                <DialogTitle disableTypography>
                    <div className={classes.dialogTitle}>
                        <Typography variant="h6">{editingGoalEventID ? 'Modifier un but' : 'Ajouter un but'}</Typography>
                        <Tooltip title={manualTimeEdit ? 'Verrouiller les temps' : 'Modifier les temps'}>
                            <IconButton onClick={() => setManualTimeEdit(!manualTimeEdit)}>
                                {manualTimeEdit ? <DoneIcon /> : <EditIcon />}
                            </IconButton>
                        </Tooltip>
                    </div>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        select
                        margin="dense"
                        fullWidth
                        label="Equipe"
                        value={selectedTeamID}
                        onChange={(e) => {
                            setSelectedTeamID(parseInt(e.target.value, 10));
                            setSelectedPlayerID('');
                            setSelectedAssistPlayerID1('');
                            setSelectedAssistPlayerID2('');
                        }}
                    >
                        {teamPlayers.map((x) => (
                            <MenuItem key={x.team ? x.team.teamID : 'none'} value={x.team ? x.team.teamID : ''}>
                                {x.team ? x.team.name : ''}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select
                        margin="dense"
                        fullWidth
                        label="Joueur"
                        value={selectedPlayerID}
                        onChange={(e) => setSelectedPlayerID(parseInt(e.target.value, 10))}
                    >
                        {playersForTeam.map((p) => (
                            <MenuItem key={p.playerID} value={p.playerID}>
                                {`${p.name}${p.defaultNumber ? ' #' + p.defaultNumber : ''}`}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select
                        margin="dense"
                        fullWidth
                        label="Assistance 1"
                        value={selectedAssistPlayerID1}
                        onChange={(e) => setSelectedAssistPlayerID1(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                    >
                        <MenuItem value="">Aucune</MenuItem>
                        {assistPlayersForTeam.map((p) => (
                            <MenuItem key={`assist1_${p.playerID}`} value={p.playerID}>
                                {`${p.name}${p.defaultNumber ? ' #' + p.defaultNumber : ''}`}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select
                        margin="dense"
                        fullWidth
                        label="Assistance 2"
                        value={selectedAssistPlayerID2}
                        onChange={(e) => setSelectedAssistPlayerID2(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                    >
                        <MenuItem value="">Aucune</MenuItem>
                        {assistPlayersForTeam.filter(p => p.playerID !== selectedAssistPlayerID1).map((p) => (
                            <MenuItem key={`assist2_${p.playerID}`} value={p.playerID}>
                                {`${p.name}${p.defaultNumber ? ' #' + p.defaultNumber : ''}`}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        margin="dense"
                        fullWidth
                        type="number"
                        label="Periode"
                        value={period}
                        disabled={!manualTimeEdit}
                        onChange={(e) => setPeriod(parseInt(e.target.value || '1', 10))}
                    />

                    <TextField
                        margin="dense"
                        fullWidth
                        label="Temps relatif (HH:MM:SS)"
                        value={timeText}
                        disabled={!manualTimeEdit}
                        onChange={(e) => setTimeText(e.target.value)}
                    />

                    <Typography variant="caption" color="textSecondary" className={classes.helperText}>
                        Suggestion automatique: {formatMillisToHhMmSs(getSuggestedTimeMs(period))} (temps actuel + 1:00)
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setGoalDialogOpen(false); setEditingGoalEventID(null); }}>Annuler</Button>
                    <Button color="primary" variant="contained" onClick={submitGoal} disabled={!selectedTeamID || !selectedPlayerID}>
                        {editingGoalEventID ? 'Enregistrer' : 'Ajouter'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={presenceDialogOpen} onClose={closePresenceDialog} fullWidth maxWidth="sm">
                <DialogTitle>Ajouter un joueur à l'alignement</DialogTitle>
                <DialogContent>
                    <TextField
                        select
                        margin="dense"
                        fullWidth
                        label="Equipe"
                        value={selectedPresenceDomVis}
                        onChange={(e) => setSelectedPresenceDomVis(parseInt(e.target.value, 10))}
                    >
                        {(presenceDialogData.teams || []).map((x) => (
                            <MenuItem key={`presence_team_${x.domVis}`} value={x.domVis}>
                                {x.team ? x.team.name : ''}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Autocomplete
                        options={presencePlayerOptions}
                        value={selectedPresencePlayer}
                        inputValue={presencePlayerSearch}
                        loading={isSearchingPresencePlayers}
                        getOptionLabel={(option) => formatPresencePlayerLabel(option)}
                        getOptionSelected={(option, value) => option.playerID === value.playerID}
                        onChange={(e, value) => setSelectedPresencePlayer(value)}
                        onInputChange={(e, value) => setPresencePlayerSearch(value || '')}
                        noOptionsText="Aucun joueur"
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                margin="dense"
                                fullWidth
                                label="Joueur de la ligue"
                                placeholder="Nom ou numéro"
                            />
                        )}
                    />

                    <TextField
                        select
                        margin="dense"
                        fullWidth
                        label="Position"
                        value={selectedPresencePositionID}
                        onChange={(e) => setSelectedPresencePositionID(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                    >
                        <MenuItem value="">Aucune</MenuItem>
                        {(presenceDialogData.positions || []).map((x) => (
                            <MenuItem key={`presence_position_${x.positionID}`} value={x.positionID}>
                                {`${localizeText(x.name)}${x.code ? ' (' + localizeText(x.code) + ')' : ''}`}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        margin="dense"
                        fullWidth
                        type="number"
                        label="Statut"
                        value={presenceStatus}
                        onChange={(e) => setPresenceStatus(parseInt(e.target.value || '100', 10))}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closePresenceDialog}>Annuler</Button>
                    <Button color="primary" variant="contained" onClick={submitPresence} disabled={!selectedPresenceDomVis || !selectedPresencePlayer}>
                        Ajouter
                    </Button>
                </DialogActions>
            </Dialog>

        </>
    )
}
export default Summary
